import React, { useEffect, useMemo, useReducer } from 'react';
import { useRouter } from 'next/compat/router';
import { map } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type { Dispensaries, ProductDemandCount } from 'src/gql/graphql';
import type CartItem from 'state/cart-item';

import {
  afterHoursEnabledForDelivery,
  afterHoursEnabledForPickup,
  scheduledOrderingEnabledForDelivery,
  getDeliveryMinimum,
  getPickupMinimum,
} from 'shared/helpers/dispensaries';
import { openInfoForDispensary } from 'shared/core/helpers/dispensaries';
import useErnie from 'shared/hooks/use-ernie';
import useCart from 'hooks/use-cart';
import useUI from 'hooks/use-ui';
import useGoToCheckout from 'hooks/use-go-to-checkout';
import useCustomerState from 'hooks/use-customer-state';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';

import { cartItemId } from 'utils/helpers/product';
import useAppliedOffers from 'hooks/use-applied-offers';
import useProductDemand from './product-demand/use-product-demand';

export type DropdownType = 'orderType' | null;

export type DropdownState = {
  openDropdown?: DropdownType;
};

export type DropdownAction = {
  openDropdown: DropdownType;
};

export type OrderTypeStateHandlers = {
  handleCloseOrderTypeDropdown: () => void;
  handleOpenOrderTypeDropdown: () => void;
  isOrderTypeDropdownOpen: boolean;
};

export type UseCartDropdownOptions = {
  onClose: () => void;
};

export type ChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { value: unknown } };

export type UseCartDropdownReturn = {
  appliedOffers: ReturnType<typeof useAppliedOffers>['appliedOffers'];
  discountToCartApplied: boolean;
  dispensary: Dispensaries | null;
  displayOrderTypeSwitch: boolean | undefined;
  durationEstimates: ReturnType<typeof useCart>['durationEstimates'];
  handleCloseOrderTypeDropdown: () => void;
  handleGoToCheckout: () => void;
  handleOpenOrderTypeDropdown: () => void;
  handleProductDemandChange: (productId: string, productDemandCount: ProductDemandCount | undefined) => void;
  handleQuantityChange: (item: CartItem) => (event: ChangeEvent) => void;
  handleRemoveItem: (item: CartItem) => () => void;
  handleWeightChange: (item: CartItem) => (e: ChangeEvent) => void;
  hasNotMetMinimum: boolean;
  isCalculating: boolean;
  isEmbedded: boolean;
  isKiosk: boolean;
  isOrderTypeDropdownOpen: boolean;
  itemCount: number;
  items: CartItem[] | Record<string, CartItem | undefined>;
  menuType: string;
  minimum: number;
  showAdsOnlyCarousel: boolean;
  showEstimate: boolean | null | undefined;
  showShop: boolean;
  simplifiedOrderType: string;
  subtotal: number;
  subtotalMinusDiscounts: number;
  taxInclusionNote: string;
  visibleProductDemandCounts: string[];
};

function dropdownReducer(state: DropdownState, action: DropdownAction): DropdownState {
  if (state.openDropdown === action.openDropdown) {
    return { openDropdown: null };
  }
  return { openDropdown: action.openDropdown };
}

function useOrderTypeStateHandlers(): OrderTypeStateHandlers {
  // TODO: concider simplifing by using useState
  const [{ openDropdown }, dispatch] = useReducer(dropdownReducer, {});

  const handleOpenOrderTypeDropdown = (): void => {
    dispatch({ openDropdown: 'orderType' });
  };

  const handleCloseOrderTypeDropdown = (): void => {
    dispatch({ openDropdown: null });
  };

  const isOrderTypeDropdownOpen = openDropdown === 'orderType';

  return {
    handleOpenOrderTypeDropdown,
    handleCloseOrderTypeDropdown,
    isOrderTypeDropdownOpen,
  };
}

export function useCartDropdown({ onClose }: UseCartDropdownOptions): UseCartDropdownReturn {
  const {
    dispensary,
    durationEstimates,
    isDelivery,
    isPickup,
    itemCount,
    items,
    menuType,
    simplifiedOrderType,
    subtotal,
    subtotalMinusDiscounts,
    taxInclusionNote,
    changeItemWeight,
    changeItemQuantity,
    removeItem,
    isEmpty,
    isCalculating,
  } = useCart();

  const { isEmbedded, isKiosk, showShop, variant } = useUI();
  const flags = useFlags();

  const enableAdsOnlyCarousel =
    flags['growth.ads.sponsored-products-only-in-cart-recommendation-carousel.config']?.[variant] ?? false;

  const adsEnabled = useAdsEnabled();

  const showAdsOnlyCarousel = useMemo(() => adsEnabled && enableAdsOnlyCarousel, [adsEnabled, enableAdsOnlyCarousel]);

  const showErnie = useErnie();
  const goToCheckout = useGoToCheckout(`in-cart`);

  const {
    handleOpenOrderTypeDropdown,
    handleCloseOrderTypeDropdown,
    isOrderTypeDropdownOpen,
  } = useOrderTypeStateHandlers();

  const customerState = useCustomerState();
  const router = useRouter();

  const { visibleProductDemandCounts, handleProductDemandChange } = useProductDemand({
    productIds: map(items, 'id'),
  });

  const handleGoToCheckout = (): void => {
    if (goToCheckout()) {
      onClose();
    }
  };

  const openInfo = openInfoForDispensary(dispensary);
  const offerAnyPickupService = dispensary?.orderTypesConfigV2?.offerAnyPickupService;
  const offerDelivery = dispensary?.orderTypesConfigV2?.offerDeliveryService;
  const afterHoursOrderingForPickup = afterHoursEnabledForPickup(dispensary);
  const afterHoursOrderingForDelivery = afterHoursEnabledForDelivery(dispensary);
  const scheduledOrderingForDelivery = scheduledOrderingEnabledForDelivery(dispensary);

  const { discountToCartApplied, appliedOffers } = useAppliedOffers();

  const { inStorePickup, curbsidePickup, driveThruPickup, delivery } = openInfo;

  const pickupIsOpen = inStorePickup.isOpen || curbsidePickup.isOpen || driveThruPickup.isOpen;
  const deliveryIsOpen = delivery.isOpen;

  let displayOrderTypeSwitch: boolean | undefined;

  if (isKiosk) {
    displayOrderTypeSwitch = false;
  } else if (dispensary) {
    if (isDelivery) {
      displayOrderTypeSwitch = offerAnyPickupService && (pickupIsOpen || afterHoursOrderingForPickup);
    } else if (isPickup) {
      displayOrderTypeSwitch =
        dispensary.deliveryInfo?.withinBounds &&
        offerDelivery &&
        (deliveryIsOpen || (afterHoursOrderingForDelivery && scheduledOrderingForDelivery));
    }
  }

  const showEstimate =
    dispensary && offerDelivery && !dispensary.featureFlags?.hideDeliveryEstimate && !scheduledOrderingForDelivery;

  const handleWeightChange = (item: CartItem) => (e: ChangeEvent) => {
    const { value } = e.target as { value: string };
    changeItemWeight(item, value, showErnie, customerState);
  };

  const handleQuantityChange = (item: CartItem) => (event: ChangeEvent) => {
    const id = cartItemId(item);
    const value = Number(event.target.value);
    changeItemQuantity(id, value, showErnie, customerState);
  };

  const handleRemoveItem = (item: CartItem) => () => {
    removeItem(item.key);
  };

  let hasNotMetMinimum = false;
  let minimum = 0;

  if (!isEmpty && dispensary) {
    minimum = isDelivery ? getDeliveryMinimum(dispensary, true) : getPickupMinimum(dispensary, `inStorePickup`);
    // Same pre-tax basis as the submit validation (checkout/actions/validate-submit.js),
    // so the minimum warning appears exactly when submit would block. Under OTD pricing
    // `subtotal` is the tax-inclusive display figure.
    hasNotMetMinimum = minimum > subtotalMinusDiscounts;
  }

  useEffect(() => {
    router?.events.on('routeChangeComplete', onClose);

    return () => {
      router?.events.off('routeChangeComplete', onClose);
    };
  }, [router, onClose]);

  return {
    appliedOffers,
    discountToCartApplied,
    dispensary,
    displayOrderTypeSwitch,
    durationEstimates,
    handleCloseOrderTypeDropdown,
    handleGoToCheckout,
    handleOpenOrderTypeDropdown,
    handleQuantityChange,
    handleRemoveItem,
    handleWeightChange,
    hasNotMetMinimum,
    isCalculating,
    isEmbedded,
    isKiosk,
    isOrderTypeDropdownOpen,
    itemCount,
    items,
    menuType,
    minimum,
    showEstimate,
    showShop,
    simplifiedOrderType,
    subtotal,
    subtotalMinusDiscounts,
    taxInclusionNote,
    visibleProductDemandCounts,
    handleProductDemandChange,
    showAdsOnlyCarousel,
  };
}
